import { AddressDto } from "@modules/address/addressDto"
import { PhoneNumberWithPrefix } from "@modules/box/boxTypes"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import {
  DiscountType,
  TransactionDetailsDto,
  TransactionDiscountDto,
  TransactionLineDto,
  TransactionUserDetailsDto,
} from "@modules/finances/gateway/transactionDto"
import {
  DisputeReason,
  DisputeStatus,
  PaymentMethodType,
  TransactionStatus,
} from "@modules/finances/types/financesType"
import { HUImage } from "@modules/types/imageTypes"

export class TransactionDiscountEntity {
  amount: number
  currency: string | null
  couponType: CouponType
  name: string | null
  type: DiscountType

  private constructor(transactionDiscount: TransactionDiscountEntity) {
    this.amount = transactionDiscount.amount
    this.currency = transactionDiscount.currency
    this.couponType = transactionDiscount.couponType
    this.name = transactionDiscount.name
    this.type = transactionDiscount.type
  }

  static fromDto(transactionDiscountDto: TransactionDiscountDto): TransactionDiscountEntity {
    return new TransactionDiscountEntity({
      amount: transactionDiscountDto.amount,
      currency: transactionDiscountDto.currency,
      couponType: transactionDiscountDto.couponType,
      name: transactionDiscountDto.name,
      type: transactionDiscountDto.type,
    })
  }
}

export class TransactionLineEntity {
  description: string | null
  netAmount: number
  taxAmount: number | null
  totalAmount: number
  taxPercent: number | null
  currency: string
  periodStart: Date | null
  periodEnd: Date | null

  private constructor(transactionLine: TransactionLineEntity) {
    this.description = transactionLine.description
    this.netAmount = transactionLine.netAmount
    this.taxAmount = transactionLine.taxAmount
    this.totalAmount = transactionLine.totalAmount
    this.taxPercent = transactionLine.taxPercent
    this.currency = transactionLine.currency
    this.periodStart = transactionLine.periodStart
    this.periodEnd = transactionLine.periodEnd
  }

  static fromDto(transactionLineDto: TransactionLineDto): TransactionLineEntity {
    return new TransactionLineEntity({
      description: transactionLineDto.description,
      netAmount: transactionLineDto.netAmount,
      taxAmount: transactionLineDto.taxAmount,
      totalAmount: transactionLineDto.totalAmount,
      taxPercent: transactionLineDto.taxPercent,
      currency: transactionLineDto.currency,
      periodStart: transactionLineDto.periodStart ? new Date(transactionLineDto.periodStart) : null,
      periodEnd: transactionLineDto.periodEnd ? new Date(transactionLineDto.periodEnd) : null,
    })
  }
}

export class TransactionUserDetailsEntity {
  id: string
  boxMemberNumber: string | null
  firstname: string
  lastname: string
  username: string
  avatar: HUImage | null
  address: AddressDto | null
  email: string
  nationality: string | null
  phoneNumber: PhoneNumberWithPrefix | null
  stripeCustomerId: string

  private constructor(transactionUser: TransactionUserDetailsEntity) {
    this.id = transactionUser.id
    this.boxMemberNumber = transactionUser.boxMemberNumber
    this.firstname = transactionUser.firstname
    this.lastname = transactionUser.lastname
    this.username = transactionUser.username
    this.avatar = transactionUser.avatar
    this.address = transactionUser.address
    this.email = transactionUser.email
    this.nationality = transactionUser.nationality
    this.phoneNumber = transactionUser.phoneNumber
    this.stripeCustomerId = transactionUser.stripeCustomerId
  }

  static fromDto(transactionUserDto: TransactionUserDetailsDto): TransactionUserDetailsEntity {
    return new TransactionUserDetailsEntity({
      id: transactionUserDto.id,
      boxMemberNumber: transactionUserDto.boxMemberNumber,
      firstname: transactionUserDto.firstname,
      lastname: transactionUserDto.lastname,
      username: transactionUserDto.username,
      avatar: transactionUserDto.avatar ? convertImageFromDto(transactionUserDto.avatar) : null,
      address: transactionUserDto.address,
      email: transactionUserDto.email,
      nationality: transactionUserDto.nationality,
      phoneNumber: transactionUserDto.phoneNumber,
      stripeCustomerId: transactionUserDto.stripeCustomerId,
    })
  }
}

export class TransactionDetailsEntity {
  transactionId: string
  refundable: boolean
  date: Date
  amount: number
  netAmount: number
  totalTaxAmount: number
  fee: number
  currency: string
  status: TransactionStatus
  disputed: boolean
  refunded: boolean
  invoiceNumber: string
  invoicePdfUrl: string | null
  lines: TransactionLineEntity[]
  discounts: TransactionDiscountEntity[]
  user: TransactionUserDetailsEntity
  paymentMethodDetail: {
    type: PaymentMethodType
    last4Digits: string | null
  }
  disputeDetail: {
    amount: number
    fee: number
    status: DisputeStatus
    reason: DisputeReason
  } | null

  private constructor(transactionDetails: TransactionDetailsEntity) {
    this.transactionId = transactionDetails.transactionId
    this.refundable = transactionDetails.refundable
    this.date = transactionDetails.date
    this.amount = transactionDetails.amount
    this.fee = transactionDetails.fee
    this.netAmount = transactionDetails.netAmount
    this.currency = transactionDetails.currency
    this.status = transactionDetails.status
    this.disputed = transactionDetails.disputed
    this.refunded = transactionDetails.refunded
    this.totalTaxAmount = transactionDetails.totalTaxAmount
    this.invoiceNumber = transactionDetails.invoiceNumber
    this.invoicePdfUrl = transactionDetails.invoicePdfUrl
    this.lines = transactionDetails.lines
    this.discounts = transactionDetails.discounts
    this.user = transactionDetails.user
    this.paymentMethodDetail = transactionDetails.paymentMethodDetail
    this.disputeDetail = transactionDetails.disputeDetail
  }

  static fromDto(transactionDetailsDto: TransactionDetailsDto): TransactionDetailsEntity {
    return new TransactionDetailsEntity({
      transactionId: transactionDetailsDto.transactionId,
      refundable: transactionDetailsDto.refundable,
      date: new Date(transactionDetailsDto.date),
      amount: transactionDetailsDto.amount,
      fee: transactionDetailsDto.fee,
      netAmount: transactionDetailsDto.netAmount,
      currency: transactionDetailsDto.currency,
      status: transactionDetailsDto.status,
      disputed: transactionDetailsDto.disputed,
      refunded: transactionDetailsDto.refunded,
      totalTaxAmount: transactionDetailsDto.totalTaxAmount,
      invoiceNumber: transactionDetailsDto.invoiceNumber,
      invoicePdfUrl: transactionDetailsDto.invoicePdfUrl,
      lines: transactionDetailsDto.lines.map(TransactionLineEntity.fromDto),
      discounts: transactionDetailsDto.discounts.map(TransactionDiscountEntity.fromDto),
      user: TransactionUserDetailsEntity.fromDto(transactionDetailsDto.user),
      paymentMethodDetail: transactionDetailsDto.paymentMethodDetail,
      disputeDetail: transactionDetailsDto.disputeDetail,
    })
  }
}
